import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Filter16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';
import { Button, ButtonKind } from 'carbon-components-react';
import { TooltipPosition } from 'carbon-components-react/typings/shared';
import Panel from './panel';
import Utils from 'utilities/helper.util';
import {
  useComponentMount,
  useEmptyCallback,
  useEventListener
} from 'utilities/hooks.util';
import { isSafari, isMobile } from 'react-device-detect';

import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from '../../localStore/reducer';
import sagas from '../../localStore/sagas';
import { getTeamEnquirerListStart } from '../../localStore/actions';

type initialValuesType = {
  enquiryAttributes: string;
  datesSelection: string;
  datesSelectionCustomed: boolean;
  startDate: any;
  endDate: any;
  status: string[];
  userReference: string;
  enquirer: any;
};

const initialValues: initialValuesType = {
  enquiryAttributes: 'enquiry_at',
  datesSelection: 'last_30',
  datesSelectionCustomed: false,
  startDate: undefined,
  endDate: undefined,
  status: ['fulfilled', 'waiting', 'expired'],
  userReference: '',
  enquirer: undefined
};

const SearchFilter: React.FC<Props> = (props) => {
  const {
    buttonKind,
    buttonClassName,
    buttonDisabled,
    buttonIconDescription,
    buttonTooltipPosition,
    onOpen,
    open,
    user,
    mainContainerRef,
    setSearchFilter,
    showMyEnquiriesOnly,
    currentTeamId,
    setCurrentPage,
    setPageOffset
  } = props;

  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { teamEnquirerList, isLoadingTeamEnquirer } = localStore;
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState<Boolean>(false);
  const [filterValues, setFilterValues] =
    useState<initialValuesType>(initialValues);
  const [parentOverflow, setParentOverflow] = useState(false);
  const [enquirerOption, setEnquirerOption] = useState<Array<any>>([]);

  const convertFilterValues = (filterValues: initialValuesType) => {
    const {
      enquiryAttributes,
      status,
      userReference,
      startDate,
      endDate,
      datesSelection,
      datesSelectionCustomed,
      enquirer
    } = filterValues;

    let startValue = Utils.getDateBeforeDays(29).startOf('day').utc().format();
    let endValue = Utils.getNewDate().endOf('day').utc().format();

    let rangeValue = {
      type: enquiryAttributes,
      date: {
        value: datesSelection,
        start: startValue,
        end: endValue
      }
    };

    if (datesSelectionCustomed) {
      startValue = startDate.startOf('day').utc().format();
      endValue = endDate.endOf('day').utc().format();
      rangeValue = {
        type: enquiryAttributes,
        date: {
          value: 'custom',
          start: startValue,
          end: endValue
        }
      };
    }

    if (datesSelection && !datesSelectionCustomed) {
      switch (datesSelection) {
        case 'today':
          startValue = Utils.getNewDate().startOf('day').utc().format();
          endValue = Utils.getNewDate().endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'today',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'last_7':
          startValue = Utils.getDateBeforeDays(6).startOf('day').utc().format();
          endValue = Utils.getNewDate().endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'last_7',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'last_30':
          startValue = Utils.getDateBeforeDays(29)
            .startOf('day')
            .utc()
            .format();
          endValue = Utils.getNewDate().endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'last_30',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'last_90':
          startValue = Utils.getDateBeforeDays(89)
            .startOf('day')
            .utc()
            .format();
          endValue = Utils.getNewDate().endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'last_90',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'next_7':
          startValue = Utils.getNewDate().startOf('day').utc().format();
          endValue = Utils.getDateAfterDays(6).endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'next_7',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'next_30':
          startValue = Utils.getNewDate().startOf('day').utc().format();
          endValue = Utils.getDateAfterDays(29).endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'next_30',
              start: startValue,
              end: endValue
            }
          };
          break;
        case 'next_90':
          startValue = Utils.getNewDate().startOf('day').utc().format();
          endValue = Utils.getDateAfterDays(89).endOf('day').utc().format();
          rangeValue = {
            type: enquiryAttributes,
            date: {
              value: 'next_90',
              start: startValue,
              end: endValue
            }
          };
          break;
      }
    }

    return {
      userReference,
      status,
      range: rangeValue,
      enquirer
    };
  };

  const resetFilterValues = () => {
    const initialFilterStatus = ['fulfilled', 'waiting', 'expired'];
    typeof setCurrentPage === 'function' && setCurrentPage(1);
    typeof setPageOffset === 'function' && setPageOffset(0);
    const values = convertFilterValues(initialValues);
    typeof setSearchFilter === 'function' &&
      setSearchFilter({
        ...values,
        status: initialFilterStatus
      });
    setFilterValues({
      ...initialValues,
      status: initialFilterStatus
    });
    setIsOpen(!isOpen);
  };

  const applyFilter = (updateValues: any) => {
    typeof setCurrentPage === 'function' && setCurrentPage(1);
    typeof setPageOffset === 'function' && setPageOffset(0);
    const values = convertFilterValues(updateValues);
    typeof setSearchFilter === 'function' && setSearchFilter(values);
    setFilterValues(updateValues);
    setIsOpen(!isOpen);
  };

  const onClickOpenButton = () => {
    if (filterButtonRef !== undefined) {
      const filterButtonElement = filterButtonRef.current;
      filterButtonElement && filterButtonElement.blur();

      if (typeof open !== 'boolean') {
        setIsOpen(!isOpen);
      }
      if (typeof onOpen === 'function') {
        onOpen();
      }
    }
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (
          !containerRef.current.contains(evt.target) &&
          !calendarRef.current
        ) {
          setIsOpen(false);
        }
      }
    },
    [containerRef, setIsOpen]
  );

  const getTeamEnquirerList = useEmptyCallback((teamId: number) => {
    setEnquirerOption([]);
    localDispatch(getTeamEnquirerListStart({ teamId }));
  });

  const checkParentOverflow = useCallback(() => {
    if (mainContainerRef !== undefined) {
      const mainContainerElement = mainContainerRef.current;
      if (mainContainerElement) {
        if (
          mainContainerElement.scrollHeight > mainContainerElement.clientHeight
        ) {
          setParentOverflow(true);
        }
      }
    }
  }, [mainContainerRef, setParentOverflow]);

  useEffect(() => {
    if (typeof open === 'boolean') {
      setIsOpen(open);
    }
  }, [open]);

  useEffect(() => {
    if (isOpen && !isMobile && !isSafari) {
      checkParentOverflow();
    } else {
      setParentOverflow(false);
    }
  }, [isOpen, checkParentOverflow, setParentOverflow]);

  useEffect(() => {
    if (currentTeamId && !showMyEnquiriesOnly) {
      getTeamEnquirerList(currentTeamId);
    } else {
      setEnquirerOption([]);
    }
  }, [currentTeamId, showMyEnquiriesOnly, getTeamEnquirerList]);

  useEffect(() => {
    if (!isLoadingTeamEnquirer && teamEnquirerList?.length > 0) {
      const filteredList = teamEnquirerList
        .map((enq: any) => {
          return {
            label: `${enq.first_name} ${enq.last_name}`,
            value: enq.id
          };
        })
        .sort((itemA: any, itemB: any) =>
          itemA.label.localeCompare(itemB.label)
        );
      filteredList.unshift({ label: 'Any enquirer (default)', value: '0' });
      setEnquirerOption(filteredList);
    }
  }, [teamEnquirerList, isLoadingTeamEnquirer]);

  useComponentMount(() => {
    const values = convertFilterValues(filterValues);
    typeof setSearchFilter === 'function' && setSearchFilter(values);
  });

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <div className="home--search-filter-container">
      <Button
        renderIcon={Filter16}
        iconDescription={
          buttonIconDescription === null ? undefined : buttonIconDescription
        }
        tooltipPosition={buttonTooltipPosition}
        hasIconOnly
        size="field"
        kind={buttonKind}
        disabled={buttonDisabled === null ? undefined : buttonDisabled}
        className={buttonClassName === null ? undefined : buttonClassName}
        onClick={() => onClickOpenButton()}
        ref={filterButtonRef}
      />
      {isOpen && (
        <Panel
          handleFilter={applyFilter}
          filterValues={filterValues}
          resetValues={resetFilterValues}
          setFilterValues={(v: any) => {
            setFilterValues(v);
          }}
          handleOpenCloseFilter={(open: boolean) => setIsOpen(open)}
          user={user}
          parentOverflow={parentOverflow}
          containerRef={containerRef}
          currentTeamId={currentTeamId}
          enquirerOption={enquirerOption}
          calendarRef={calendarRef}
        />
      )}
    </div>
  );
};

const propTypes = {
  buttonDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttonIconDescription: PropTypes.string,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  dateRangeText: PropTypes.string,
  user: PropTypes.any,
  mainContainerRef: PropTypes.any,
  isListView: PropTypes.bool,
  setSearchFilter: PropTypes.func,
  showMyEnquiriesOnly: PropTypes.bool,
  currentTeamId: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setPageOffset: PropTypes.func
};

type carbinProps = {
  buttonKind?: ButtonKind;
  buttonTooltipPosition?: TooltipPosition;
};

type Props = PropTypes.InferProps<typeof propTypes> & carbinProps;

SearchFilter.defaultProps = {
  buttonKind: 'tertiary',
  buttonDisabled: false,
  buttonClassName: 'search-filter-panel-button-before',
  buttonIconDescription: 'Filter',
  buttonTooltipPosition: 'bottom',
  onOpen: () => {}
};

export default React.memo(SearchFilter);
